import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface Banner {
  id: string;
  text: string;
  backgroundColor: 'jamaica-green' | 'jamaica-black';
  isActive: boolean;
  order: number; // 1 or 2 for ordering
  link?: string; // Optional link to navigate when banner is clicked
  showCloseButton?: boolean; // Whether to show the close button
  countdown?: {
    enabled: boolean;
    endDate: string | Timestamp; // ISO date string or Firebase Timestamp
    format?: 'days' | 'hours' | 'minutes' | 'full'; // Display format
    prefix?: string; // Text before countdown (e.g., "Ends in:")
    suffix?: string; // Text after countdown (e.g., "remaining")
  };
}

export interface BannerConfig {
  banner1?: Banner;
  banner2?: Banner;
}

/**
 * Get banner configuration from Firestore
 */
export const getBannerConfig = async (): Promise<BannerConfig> => {
  try {
    const bannerRef = doc(db, 'config', 'banners');
    const bannerDoc = await getDoc(bannerRef);
    
    if (!bannerDoc.exists()) {
      return {};
    }
    
    const data = bannerDoc.data();
    const config: BannerConfig = {};
    
    // Process banner1
    if (data.banner1 && data.banner1.isActive) {
      config.banner1 = {
        id: 'banner1',
        text: data.banner1.text || '',
        backgroundColor: data.banner1.backgroundColor || 'jamaica-green',
        isActive: data.banner1.isActive || false,
        order: 1,
        link: data.banner1.link || undefined,
        showCloseButton: data.banner1.showCloseButton ?? false, // Default to false if not specified
        countdown: data.banner1.countdown
      };
    }
    
    // Process banner2
    if (data.banner2 && data.banner2.isActive) {
      config.banner2 = {
        id: 'banner2',
        text: data.banner2.text || '',
        backgroundColor: data.banner2.backgroundColor || 'jamaica-black',
        isActive: data.banner2.isActive || false,
        order: 2,
        link: data.banner2.link || undefined,
        showCloseButton: data.banner2.showCloseButton ?? false, // Default to false if not specified
        countdown: data.banner2.countdown
      };
    }
    
    return config;
  } catch (error) {
    console.error('Error fetching banner config:', error);
    return {};
  }
};

/**
 * Subscribe to banner configuration changes
 */
export const subscribeToBannerConfig = (
  callback: (config: BannerConfig) => void
): (() => void) => {
  const bannerRef = doc(db, 'config', 'banners');
  
  return onSnapshot(bannerRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      const config: BannerConfig = {};
      
      // Process banner1
      if (data.banner1 && data.banner1.isActive) {
        config.banner1 = {
          id: 'banner1',
          text: data.banner1.text || '',
          backgroundColor: data.banner1.backgroundColor || 'jamaica-green',
          isActive: data.banner1.isActive || false,
          order: 1,
          link: data.banner1.link || undefined,
          showCloseButton: data.banner1.showCloseButton ?? false, // Default to false if not specified
          countdown: data.banner1.countdown
        };
      }
      
      // Process banner2
      if (data.banner2 && data.banner2.isActive) {
        config.banner2 = {
          id: 'banner2',
          text: data.banner2.text || '',
          backgroundColor: data.banner2.backgroundColor || 'jamaica-black',
          isActive: data.banner2.isActive || false,
          order: 2,
          link: data.banner2.link || undefined,
          showCloseButton: data.banner2.showCloseButton ?? false, // Default to false if not specified
          countdown: data.banner2.countdown
        };
      }
      
      callback(config);
    } else {
      callback({});
    }
  }, (error) => {
    console.error('Error subscribing to banner config:', error);
    callback({});
  });
}; 
